import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Card,
  Row,
  Col,
  Badge,
  Table,
  Container,
  Tabs,
  Tab,
} from "react-bootstrap";
import { Link, useParams } from "react-router-dom";
import {
  FaBox,
  FaUser,
  FaCalendarAlt,
  FaProjectDiagram,
  FaIndustry,
  FaShoppingCart,
  FaCogs,
  FaCheckCircle,
  FaClock,
  FaExclamationTriangle,
} from "react-icons/fa";
import config from "../../config";
import Layout from "../../components/layouts/Main";
import { PulseLoader } from "react-spinners";

function ProductionRequestDetail() {
  const { id } = useParams();
  const [request, setRequest] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchData() {
      try {
        const res = await axios.get(
          `${config.API_BASE_URL}/production-requests/${id}`
        );
        setRequest(res.data);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, [id]);

  const formatDate = (dateStr) =>
    dateStr
      ? new Date(dateStr).toLocaleDateString("en-IN", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        })
      : "—";

  const getStatusVariant = (status) => {
    switch (status) {
      case "COMPLETED":
        return "success";
      case "IN_PROGRESS":
        return "warning";
      case "PENDING":
        return "secondary";
      case "REJECTED":
        return "danger";
      default:
        return "light";
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="text-center py-5">
          <PulseLoader color="#014b7cbb" size={9} />
        </div>
      </Layout>
    );
  }

  if (!request) {
    return (
      <Layout>
        <Container fluid className="py-5 text-center">
          <p className="text-muted">No request details available.</p>
        </Container>
      </Layout>
    );
  }

  return (
    <Layout>
      {/* Breadcrumb */}
      <nav aria-label="breadcrumb" className="mb-3">
        <ol className="breadcrumb">
          <li className="breadcrumb-item">
            <Link to="/production-requests">Production Requests</Link>
          </li>
          <li className="breadcrumb-item active">{request.requestNumber}</li>
        </ol>
      </nav>
      <Container fluid className="my-4">
        {/* Header */}
        <div className="d-flex justify-content-between align-items-center mb-4">
          {/* Left side */}
          <div className="d-flex align-items-center">
            <div>
              <h3 className="mb-1 d-flex align-items-center">
                {request.requestNumber}
              </h3>
            </div>
            <div className="d-flex align-items-center gap-2 ms-2">
              <Badge bg="info" variant="outline">
                {request.requestType}
              </Badge>
              {request.requestType === "SALES_ORDER" &&
                request.salesOrders?.length > 0 && (
                  <Badge bg="secondary" variant="outline">
                    {request.salesOrders.length} Sales Orders
                  </Badge>
                )}
            </div>
          </div>

          {/* Right side */}
          <div>
            <div className="">
              <Badge
                size="md"
                bg={getStatusVariant(request.status)}
                className="d-flex align-items-center gap-1"
              >
                {request.status === "COMPLETED" ? (
                  <FaCheckCircle size={12} />
                ) : request.status === "IN_PROGRESS" ? (
                  <FaClock size={12} />
                ) : (
                  <FaExclamationTriangle size={12} />
                )}
                {request.status}
              </Badge>
            </div>
          </div>
        </div>

        {/* Summary Cards */}
        <Row className="mb-4">
          <Col md={3} sm={6} className="mb-3">
            <Card className="h-100 border-0 shadow-sm">
              <Card.Body className="text-center">
                <FaBox className="text-primary mb-2" size={24} />
                <h6 className="text-muted mb-1">Product</h6>
                <p className="mb-0 fw-bold">{request.productId?.productName}</p>
                <small className="text-muted">{request.productId?.alias}</small>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3} sm={6} className="mb-3">
            <Card className="h-100 border-0 shadow-sm">
              <Card.Body className="text-center">
                <FaCogs className="text-success mb-2" size={24} />
                <h6 className="text-muted mb-1">Quantity</h6>
                <p className="mb-0 fw-bold">
                  {request.quantityRequired.toLocaleString()}
                </p>
                <small className="text-muted">
                  {request.productId?.unitId?.unitCode}
                </small>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3} sm={6} className="mb-3">
            <Card className="h-100 border-0 shadow-sm">
              <Card.Body className="text-center">
                <FaCalendarAlt className="text-warning mb-2" size={24} />
                <h6 className="text-muted mb-1">Required Date</h6>
                <p className="mb-0 fw-bold">
                  {formatDate(request.requiredDate)}
                </p>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3} sm={6} className="mb-3">
            <Card className="h-100 border-0 shadow-sm">
              <Card.Body className="text-center">
                <FaUser className="text-info mb-2" size={24} />
                <h6 className="text-muted mb-1">Requested By</h6>
                <p className="mb-0 fw-bold">{request.requestedBy?.name}</p>
                <small className="text-muted">
                  {request.requestedBy?.email}
                </small>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Tabbed Content */}
        <Card className="shadow-sm border-0">
          <Card.Body>
            <Tabs defaultActiveKey="product" className="mb-4">
              <Tab
                eventKey="product"
                title={
                  <>
                    <FaBox className="me-1" />
                    Product Details
                  </>
                }
              >
                <Row>
                  <Col md={6}>
                    <div className="info-item mb-3">
                      <label className="text-muted small">Product Name</label>
                      <p className="fw-bold mb-0">
                        {request.productId?.productName}
                      </p>
                      <small className="text-muted">
                        {request.productId?.alias}
                      </small>
                    </div>
                    <div className="info-item mb-3">
                      <label className="text-muted small">Product Code</label>
                      <p className="fw-bold mb-0">
                        {request.productId?.productCode}
                      </p>
                    </div>
                    <div className="info-item mb-3">
                      <label className="text-muted small">Category</label>
                      <p className="fw-bold mb-0">
                        {request.productId?.category}
                      </p>
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="info-item mb-3">
                      <label className="text-muted small">
                        Quantity Required
                      </label>
                      <p className="fw-bold mb-0">
                        {request.quantityRequired.toLocaleString()}{" "}
                        {request.productId?.unitId?.unitCode}
                      </p>
                    </div>
                    <div className="info-item mb-3">
                      <label className="text-muted small">SKU</label>
                      <p className="fw-bold mb-0">{request.productId?.sku}</p>
                    </div>
                    <div className="info-item mb-3">
                      <label className="text-muted small">GST Rate</label>
                      <p className="fw-bold mb-0">{request.productId?.gst}%</p>
                    </div>
                  </Col>
                </Row>
              </Tab>

              {request.bomId && (
                <Tab
                  eventKey="bom"
                  title={
                    <>
                      <FaProjectDiagram className="me-1" />
                      BOM Recipe
                    </>
                  }
                >
                  <div className="mb-4">
                    <h5 className="text-primary">{request.bomId?.name}</h5>
                    <p className="text-muted">{request.bomId?.description}</p>
                    <Badge bg="secondary">BOM ID: {request.bomId?.bomId}</Badge>
                  </div>

                  <h6 className="mb-3">Raw Materials</h6>
                  <div className="table-responsive mb-4">
                    <Table hover className="align-middle">
                      <thead className="table-light">
                        <tr>
                          <th>#</th>
                          <th>Material</th>
                          <th>Code</th>
                          <th>Quantity</th>
                          <th>Category</th>
                        </tr>
                      </thead>
                      <tbody>
                        {request.bomId.rawMaterials?.map((rm, i) => (
                          <tr key={rm._id}>
                            <td>{i + 1}</td>
                            <td>
                              <div>
                                <strong>{rm.materialId?.productName}</strong>
                                <br />
                                <small className="text-muted">
                                  {rm.materialId?.alias}
                                </small>
                              </div>
                            </td>
                            <td>
                              <code>{rm.materialId?.productCode}</code>
                            </td>
                            <td>
                              <Badge bg="info">
                                {rm.quantity} {rm.unit}
                              </Badge>
                            </td>
                            <td>
                              <Badge bg="secondary">
                                {rm.materialId?.category}
                              </Badge>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>

                  <h6 className="mb-3">Processing Stages</h6>
                  <div className="table-responsive">
                    <Table hover className="align-middle">
                      <thead className="table-light">
                        <tr>
                          <th>#</th>
                          <th>Stage</th>
                          <th>Machine</th>
                          <th>Capacity</th>
                          <th>Gain/Loss</th>
                        </tr>
                      </thead>
                      <tbody>
                        {request.bomId.processingStages?.map((stage, i) => (
                          <tr key={stage._id}>
                            <td>{i + 1}</td>
                            <td>
                              <strong>{stage.stageName}</strong>
                            </td>
                            <td>{stage.machine?.name || "—"}</td>
                            <td>
                              <Badge bg="success">
                                {stage.capacityPerHour} {stage.capacityUnit}/hr
                              </Badge>
                            </td>
                            <td>
                              <Badge
                                bg={stage.gainLoss >= 0 ? "success" : "danger"}
                              >
                                {stage.gainLoss}%
                              </Badge>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>

                  {request.bomId.outputs?.length > 0 && (
                    <>
                      <h6 className="mb-3 mt-4">Expected Outputs</h6>
                      <div className="table-responsive">
                        <Table hover className="align-middle">
                          <thead className="table-light">
                            <tr>
                              <th>#</th>
                              <th>Product</th>
                              <th>Percentage</th>
                              <th>Projected Output</th>
                            </tr>
                          </thead>
                          <tbody>
                            {request.bomId.outputs.map((output, i) => (
                              <tr key={output._id}>
                                <td>{i + 1}</td>
                                <td>
                                  <div>
                                    <strong>
                                      {output.productId?.productName}
                                    </strong>
                                    <br />
                                    <small className="text-muted">
                                      {output.productId?.alias}
                                    </small>
                                  </div>
                                </td>
                                <td>
                                  <Badge bg="primary">
                                    {output.percentage}%
                                  </Badge>
                                </td>
                                <td>
                                  <Badge bg="info">
                                    {output.projectedOutput}{" "}
                                    {output.productId?.unitId?.unitCode}
                                  </Badge>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </div>
                    </>
                  )}
                </Tab>
              )}

              {request.salesOrderId && (
                <Tab
                  eventKey="sales"
                  title={
                    <>
                      <FaShoppingCart className="me-1" />
                      Sales Order
                    </>
                  }
                >
                  <Row>
                    <Col md={6}>
                      <div className="info-item mb-3">
                        <label className="text-muted small">Order Number</label>
                        <p className="fw-bold mb-0">
                          {request.salesOrderId.orderNumber}
                        </p>
                      </div>
                      <div className="info-item mb-3">
                        <label className="text-muted small">PO Number</label>
                        <p className="fw-bold mb-0">
                          {request.salesOrderId.buyerPONumber}
                        </p>
                      </div>
                      <div className="info-item mb-3">
                        <label className="text-muted small">Status</label>
                        <Badge bg="info">{request.salesOrderId.status}</Badge>
                      </div>
                      <div className="info-item mb-3">
                        <label className="text-muted small">Order Date</label>
                        <p className="fw-bold mb-0">
                          {formatDate(request.salesOrderId.orderDate)}
                        </p>
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="info-item mb-3">
                        <label className="text-muted small">Billing To</label>
                        <p className="fw-bold mb-1">
                          {request.salesOrderId.buyerBillTo?.name}
                        </p>
                        <small className="text-muted">
                          {request.salesOrderId.buyerBillTo?.address}
                        </small>
                      </div>
                      <div className="info-item mb-3">
                        <label className="text-muted small">
                          Delivery Address
                        </label>
                        <small className="text-muted">
                          {request.salesOrderId.buyerDeliveryDetails?.address}
                        </small>
                      </div>
                      <div className="info-item mb-3">
                        <label className="text-muted small">Total Amount</label>
                        <p className="fw-bold mb-0 text-success">
                          ₹{request.salesOrderId.totalAmount?.toLocaleString()}
                        </p>
                      </div>
                    </Col>
                  </Row>
                </Tab>
              )}

              {request.rawMaterials?.length > 0 && (
                <Tab
                  eventKey="materials"
                  title={
                    <>
                      <FaIndustry className="me-1" />
                      Raw Materials
                    </>
                  }
                >
                  <div className="table-responsive">
                    <Table hover className="align-middle">
                      <thead className="table-light">
                        <tr>
                          <th>#</th>
                          <th>Material</th>
                          <th>Code</th>
                          <th>Required Quantity</th>
                          <th>Category</th>
                        </tr>
                      </thead>
                      <tbody>
                        {request.rawMaterials.map((rm, i) => (
                          <tr key={rm._id}>
                            <td>{i + 1}</td>
                            <td>
                              <div>
                                <strong>{rm.materialName}</strong>
                                <br />
                                <small className="text-muted">
                                  {rm.materialId?.alias}
                                </small>
                              </div>
                            </td>
                            <td>
                              <code>{rm.materialId?.productCode}</code>
                            </td>
                            <td>
                              <Badge bg="warning">
                                {rm.requiredQuantity.toLocaleString()} {rm.unit}
                              </Badge>
                            </td>
                            <td>
                              <Badge bg="secondary">
                                {rm.materialId?.category}
                              </Badge>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                </Tab>
              )}

              {request.requestType === "SALES_ORDER" &&
                request.salesOrders?.length > 0 && (
                  <Tab
                    eventKey="salesOrders"
                    title={
                      <>
                        <FaShoppingCart className="me-1" />
                        Sales Orders ({request.salesOrders.length})
                      </>
                    }
                  >
                    <div className="mb-3">
                      <h6 className="text-muted">Associated Sales Orders</h6>
                      <Table responsive striped>
                        <thead>
                          <tr>
                            <th>Sales Order</th>
                            {/* <th>Item</th> */}
                            <th>Quantity from Order</th>
                            {/* <th>Unit</th> */}
                            <th>Expected Delivery</th>
                          </tr>
                        </thead>
                        <tbody>
                          {request.salesOrders.map((so, idx) => (
                            <tr key={idx}>
                              <td>
                                <Link
                                  to={`/sales-orders/${so.salesOrderId?._id}`}
                                >
                                  {so.salesOrderId?.orderNumber || "N/A"}
                                </Link>
                              </td>
                              {/* <td>
                                {so.salesOrderItemId?.product?.productName ||
                                  "N/A"}
                              </td> */}
                              <td>
                                {so.quantityFromOrder}{` `}{so.salesOrderItemId?.unit ||
                                  so.salesOrderItemId?.product?.unitId
                                    ?.unitCode ||
                                  "units"}
                              </td>
                              {/* <td>
                                {so.salesOrderItemId?.unit ||
                                  so.salesOrderItemId?.product?.unitId
                                    ?.unitCode ||
                                  "units"}
                              </td> */}
                              <td>
                                {so.salesOrderId?.EDD
                                  ? formatDate(
                                      so.salesOrderId.EDD
                                    )
                                  : "N/A"}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  </Tab>
                )}
            </Tabs>
          </Card.Body>
        </Card>

        {/* Meta Info */}
        <Card className="shadow-sm border-0 mt-4">
          <Card.Body>
            <Row className="text-center">
              <Col lg={3} sm={6}>
                <div className="info-item">
                  <label className="text-muted small">Created At</label>
                  <p className="mb-0">
                    {new Date(request.createdAt).toLocaleString()}
                  </p>
                </div>
              </Col>
              <Col lg={3} sm={6}>
                <div className="info-item">
                  <label className="text-muted small">Updated At</label>
                  <p className="mb-0">
                    {new Date(request.updatedAt).toLocaleString()}
                  </p>
                </div>
              </Col>
              <Col lg={3} sm={6}>
                <div className="info-item">
                  <label className="text-muted small">Assigned To</label>
                  <p className="mb-0 fw-bold">
                    {request.assignedTo?.name || "Unassigned"}
                  </p>
                  <small className="text-muted">
                    {request.assignedTo?.email}
                  </small>
                </div>
              </Col>
              {/* Add this card after the existing 4 cards */}
              {request.requestType === "SALES_ORDER" &&
                request.salesOrders?.length > 0 && (
                  <Col lg={3} sm={6} className="mb-3">
                    <Card className="h-100 border-0 shadow-sm">
                      <Card.Body className="text-center">
                        <FaShoppingCart
                          className="text-primary mb-2"
                          size={24}
                        />
                        <h6 className="text-muted mb-1">Sales Orders</h6>
                        <p className="mb-0 fw-bold">
                          {request.salesOrders.length}
                        </p>
                        <small className="text-muted">Associated Orders</small>
                      </Card.Body>
                    </Card>
                  </Col>
                )}
            </Row>
          </Card.Body>
        </Card>

        <style jsx>{`
          .info-item {
            margin-bottom: 1rem;
          }
          .info-item label {
            display: block;
            font-weight: 600;
            text-transform: uppercase;
            letter-spacing: 0.5px;
            margin-bottom: 0.25rem;
          }
        `}</style>
      </Container>
    </Layout>
  );
}

export default ProductionRequestDetail;
